import { useCallback, useMemo, useState } from "react";

export type SelectionId = number | string;

export interface UseBulkSelectionResult<T extends { id: SelectionId }> {
  selectedIds: SelectionId[];
  isSelected: (id: SelectionId) => boolean;
  toggleOne: (id: SelectionId) => void;
  toggleAll: (items: T[]) => void;
  setAll: (items: T[]) => void;
  clearSelection: () => void;
  allSelected: boolean;
  indeterminate: boolean;
}

export function useBulkSelection<T extends { id: SelectionId }>(
  items: T[]
): UseBulkSelectionResult<T> {
  const [selectedIds, setSelectedIds] = useState<SelectionId[]>([]);

  const isSelected = useCallback(
    (id: SelectionId) => selectedIds.includes(id),
    [selectedIds]
  );

  const toggleOne = useCallback((id: SelectionId) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const setAll = useCallback((list: T[]) => {
    setSelectedIds(list.map((i) => i.id));
  }, []);

  const clearSelection = useCallback(() => setSelectedIds([]), []);

  const toggleAll = useCallback(
    (list: T[]) => {
      const allIds = list.map((i) => i.id);
      const allOn =
        list.length > 0 && allIds.every((id) => selectedIds.includes(id));
      setSelectedIds(allOn ? [] : allIds);
    },
    [selectedIds]
  );

  const allSelected = useMemo(
    () =>
      items.length > 0 &&
      items.every((i) => selectedIds.includes(i.id)),
    [items, selectedIds]
  );

  const indeterminate = useMemo(
    () => selectedIds.length > 0 && !allSelected,
    [selectedIds, allSelected]
  );

  return {
    selectedIds,
    isSelected,
    toggleOne,
    toggleAll,
    setAll,
    clearSelection,
    allSelected,
    indeterminate,
  };
}

export default useBulkSelection;
